import type { PagedResultDto } from "@services/dto/pagedResultDto"
import feeNotificationService from "@services/feeNotificatiion/feeNotificationService"
import { action, makeAutoObservable, observable } from "mobx"
import { buildRecipientIndex } from "./recipientIndex"
import type {
  BatchRecipientIndex,
  FeeNotificationHistoryItem,
} from "./recipientIndex"

/** Trần `maxResultCount` của ABP (`LimitedResultRequestDto.MaxMaxResultCount`) — bám sát để ít vòng nhất. */
const RECIPIENT_PAGE_SIZE = 1000

/**
 * Chặn vòng lặp chạy mãi nếu `totalCount` của BE lệch với số dòng thực trả về.
 * 10 trang = 10.000 dòng log, thừa sức cho một đợt thật.
 */
const RECIPIENT_MAX_PAGES = 10

class FeeNotificationStore {
  @observable isLoading!: boolean
  @observable tableBatchData!: PagedResultDto<any>
  @observable tableContractData!: PagedResultDto<any>
  @observable tableInquiryData!: PagedResultDto<any>
  @observable tableHistoryData!: PagedResultDto<any>
  @observable tableTraceabilityData!: any[]
  @observable detailData!: any
  @observable detailFeeData!: any
  @observable detailSendData!: any
  @observable batchDeliveryStats!: any

  /** Người nhận theo `feeNotificationContractId` cho bảng HĐ ở tab Thông tin của chi tiết đợt. */
  @observable batchRecipients!: BatchRecipientIndex

  /**
   * Đợt mà `batchRecipients` đang thuộc về. Cần vì mở đợt B ngay sau đợt A sẽ render trước khi
   * request của B về — không có mốc này thì email của đợt A hiện nhầm trên bảng của đợt B.
   */
  @observable batchRecipientsBatchId?: number

  /** Cờ loading RIÊNG — dùng chung `isLoading` sẽ nhấp nháy nút Gửi và bảng lịch sử gửi. */
  @observable isLoadingRecipients!: boolean

  constructor() {
    makeAutoObservable(this)

    this.tableBatchData = { items: [], totalCount: 0 }
    this.tableContractData = { items: [], totalCount: 0 }
    this.tableInquiryData = { items: [], totalCount: 0 }
    this.tableHistoryData = { items: [], totalCount: 0 }
    this.batchRecipients = {}
  }

  @action
  async getContractNotificationHistory(contractId: any, onlySent?: boolean) {
    this.isLoading = true
    const result = await feeNotificationService
      .getContractNotificationHistory(contractId, onlySent)
      .finally(() => (this.isLoading = false))
    this.tableTraceabilityData = result ?? []
  }

  // Req4: thống kê gửi cấp đợt (Delivery Dashboard).
  @action
  async getBatchDeliveryStats(batchId: any) {
    this.isLoading = true
    const result = await feeNotificationService
      .getBatchDeliveryStats(batchId)
      .finally(() => (this.isLoading = false))
    this.batchDeliveryStats = result
  }

  @action
  async create(body: any) {
    this.isLoading = true
    await feeNotificationService.create(body).finally(() => {
      this.isLoading = false
    })
  }

  @action
  async getAll(params: any) {
    this.isLoading = true
    const result = await feeNotificationService
      .getAll(params)
      .finally(() => (this.isLoading = false))
    this.tableBatchData = result
  }

  @action
  async initDetail() {
    this.detailData = {}
  }

  @action
  async get(params: any) {
    this.isLoading = true
    const result = await feeNotificationService
      .get(params)
      .finally(() => (this.isLoading = false))
    this.detailData = result
  }
  @action
  async emptyContractData() {
    this.tableContractData = { items: [], totalCount: 0 }
  }
  @action
  async getAllContractFeeNotification(params: any) {
    this.isLoading = true
    const result = await feeNotificationService
      .getAllContractFeeNotification(params)
      .finally(() => (this.isLoading = false))
    this.tableContractData = result
  }

  @action
  async emptyInquiryData() {
    this.tableInquiryData = { items: [], totalCount: 0 }
  }
  @action
  async getAllContractInquiryFeeNotification(params: any) {
    this.isLoading = true
    const result = await feeNotificationService
      .getAllContractInquiryFeeNotification(params)
      .finally(() => (this.isLoading = false))
    this.tableInquiryData = result
  }

  @action
  async getFeeNotificationHistory(params: any) {
    this.isLoading = true
    const result = await feeNotificationService
      .getFeeNotificationHistory(params)
      .finally(() => (this.isLoading = false))
    this.tableHistoryData = result
  }
  // Get detail fee
  @action
  async emptyFeeData() {
    this.detailFeeData = {}
  }
  @action
  async getContractFeeDetail(params: any) {
    this.isLoading = true
    const result = await feeNotificationService
      .getContractFeeDetail(params)
      .finally(() => (this.isLoading = false))
    this.detailFeeData = result
  }
  @action
  async emptySendData() {
    this.detailSendData = {}
  }
  @action
  async getFeeNotificationSendDetail(params: any) {
    this.isLoading = true
    const result = await feeNotificationService
      .getFeeNotificationSendDetail(params)
      .finally(() => (this.isLoading = false))
    this.detailSendData = result
  }

  // Send notification
  @action
  async sendAllNotification(params: any) {
    this.isLoading = true
    await feeNotificationService
      .sendAllFeeNotification(params)
      .finally(() => (this.isLoading = false))
  }

  // Gửi theo danh sách hợp đồng đã chọn trong chi tiết đợt.
  @action
  async sendSelectedNotification(params: {
    feeNotificationId: number
    feeNotificationContractIds: number[]
  }) {
    this.isLoading = true
    await feeNotificationService
      .sendSelectedFeeNotification(params)
      .finally(() => (this.isLoading = false))
  }
  @action
  async reSendFeeNotificationWithCustomer(body: any) {
    this.isLoading = true
    await feeNotificationService
      .reSendFeeNotificationWithCustomer(body)
      .finally(() => (this.isLoading = false))
  }

  @action 
  async deleteContractFeeNotification(params: any) {
    this.isLoading = true
    await feeNotificationService
      .deleteContractFeeNotification(params)
      .finally(() => (this.isLoading = false))
  }

  // Req1.1: export danh sách đợt theo bộ lọc
  @action
  async exportBatchList(params: any) {
    this.isLoading = true
    await feeNotificationService
      .exportBatchList(params)
      .finally(() => (this.isLoading = false))
  }

  // Req1.2: export chi tiết đợt (danh sách HĐ)
  @action
  async exportBatchDetail(batchId: any, period?: string) {
    this.isLoading = true
    await feeNotificationService
      .exportBatchDetail(batchId, period)
      .finally(() => (this.isLoading = false))
  }

  // Req1.3: export log lịch sử gửi theo bộ lọc
  @action
  async exportSendHistory(params: any, period?: string) {
    this.isLoading = true
    await feeNotificationService
      .exportSendHistory(params, period)
      .finally(() => (this.isLoading = false))
  }

  // Req2: tải hàng loạt PDF của đợt dưới dạng .zip
  @action
  async buildBatchPdfZip(batchId: any) {
    this.isLoading = true
    await feeNotificationService
      .buildBatchPdfZip(batchId)
      .finally(() => (this.isLoading = false))
  }

  // fee-notification-reporting Req 15.6: hồ sơ chứng minh đã thông báo theo HỢP ĐỒNG (.zip).
  // Hai kết cục (blob ngay / 202 đang xử lý nền) do service phân giải — store chỉ giữ cờ loading.
  @action
  async exportContractNotificationDossier(contractId: any) {
    this.isLoading = true
    await feeNotificationService
      .exportContractNotificationDossier(contractId)
      .finally(() => (this.isLoading = false))
  }

  /**
   * Nạp danh sách người nhận của CẢ đợt cho bảng "Danh sách HĐ cần thông báo" — MỘT lượt gọi cho
   * toàn đợt, KHÔNG phải một lượt cho mỗi dòng.
   *
   * <p>Dùng lại `GetFeeNotificationHistory` (endpoint log gửi cấp đợt) thay vì
   * `GetFeeNotificationSendDetail`: endpoint sau chỉ trả về một hợp đồng nên bảng vài trăm dòng sẽ
   * thành vài trăm request. Log đã có `SentTo` từ lúc tạo đợt nên endpoint này trả lời được cả đợt
   * chưa gửi — xem chú thích ở `./recipientIndex`.</p>
   *
   * <p>Ghi vào observable RIÊNG, tuyệt đối không dùng `tableHistoryData`: tab "Lịch sử gửi" đang giữ
   * kết quả phân trang + bộ lọc của người dùng ở đó, ghi đè sẽ phá bảng đó.</p>
   */
  @action
  async getBatchRecipients(batchId: number, channel: "EMAIL" | "SMS" = "EMAIL") {
    if (!batchId) return
    this.isLoadingRecipients = true
    try {
      const items = await this.fetchAllRecipientLogPages(batchId)
      this.batchRecipients = buildRecipientIndex(items, channel)
      this.batchRecipientsBatchId = batchId
    } catch {
      // Cột người nhận là thông tin phụ trợ: hỏng ở đây KHÔNG được kéo sập bảng hợp đồng.
      // Lỗi HTTP đã được interceptor của httpService surface cho người dùng; ở đây chỉ lùi về rỗng.
      this.batchRecipients = {}
      this.batchRecipientsBatchId = undefined
    } finally {
      this.isLoadingRecipients = false
    }
  }

  @action
  clearBatchRecipients() {
    this.batchRecipients = {}
    this.batchRecipientsBatchId = undefined
  }

  /**
   * Gom đủ các trang log của đợt. Endpoint trả MỘT dòng cho mỗi (hợp đồng, khách hàng) nên số dòng
   * lớn hơn số hợp đồng — đợt đồng sở hữu nhiều có thể vượt một trang.
   */
  private async fetchAllRecipientLogPages(batchId: number) {
    const collected: FeeNotificationHistoryItem[] = []
    let skipCount = 0

    for (let page = 0; page < RECIPIENT_MAX_PAGES; page++) {
      const result = await feeNotificationService.getFeeNotificationHistory({
        id: batchId,
        maxResultCount: RECIPIENT_PAGE_SIZE,
        skipCount,
      })
      const items: FeeNotificationHistoryItem[] = result?.items ?? []
      collected.push(...items)

      // Dừng khi trang rỗng (phòng `totalCount` sai) hoặc đã gom đủ theo `totalCount`.
      if (items.length === 0 || collected.length >= (result?.totalCount ?? 0)) break
      skipCount += RECIPIENT_PAGE_SIZE
    }

    return collected
  }
}

export default FeeNotificationStore
